import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import { FinalizationInboxSchema, FinalizeService } from '../models';
import { defaultValues, finalizationInboxSchema } from '../models';
import { FormSubmitButton, FormTextInput } from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAttend, useFinalize } from '../hooks';
import { IconButton, Modal } from '@mui/material';
import { useTicketFlowModals } from '../context';
import { useForm } from 'react-hook-form';
import { lastDigits } from '@/utils';

interface AttendCustomerProps {
  isOpen?: boolean;
}

export const AttendModal = ({ isOpen = true }: AttendCustomerProps) => {
  const { ticketData, setTicketData, closeModal } = useTicketFlowModals();

  const isDocumentNumberRequired =
    ticketData?.sectorId !== import.meta.env.VITE_SECTOR_CA;

  const schema = isDocumentNumberRequired
    ? finalizationInboxSchema
    : finalizationInboxSchema.omit({ documentNumber: true });

  const { onFinalizeAttention, isFinalizing } = useFinalize();

  const { control, handleSubmit, getValues, clearErrors, setError } =
    useForm<FinalizationInboxSchema>({
      resolver: zodResolver(schema),
      defaultValues,
    });

  const onSubmit = async (data: FinalizationInboxSchema, success: boolean) => {
    if (!ticketData) return;
    const { trackNumber, inboxId, sectorId } = ticketData;

    const finalizeServiceBody: FinalizeService = {
      inboxId,
      documentNumber: data?.documentNumber,
      success,
    };

    const totemSectorBody = {
      trackNumber,
      sectorId,
    };

    await onFinalizeAttention({ totemSectorBody, finalizeServiceBody });
    setTicketData(null);
    closeModal();
  };

  const onFinalizeAsIssue = () => {
    clearErrors('documentNumber');

    const values = getValues();
    const { documentNumber } = values;

    if (documentNumber && !/^\d{8}$/.test(documentNumber)) {
      setError('documentNumber', {
        type: 'manual',
        message: 'Debe contener exactamente 8 dígitos',
      });
      return;
    }

    onSubmit(values, false);
  };

  const { onCancelAttend } = useAttend();

  const handleCancelAttend = () => {
    if (!ticketData) return;

    const { inboxId, sectorId, trackNumber } = ticketData;

    onCancelAttend({
      inboxId,
      totemSectorBody: {
        sectorId,
        trackNumber,
      },
      isAlreadyTaken: ticketData.isAlreadyTaken,
    });

    closeModal();
  };

  return (
    <Modal open={isOpen} disableEscapeKeyDown>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
        <div className='flex flex-col p-10 gap-10 w-[520px] h-fit bg-white outline-none rounded-sm'>
          <div className='flex justify-between'>
            <h2 className='text-xl !font-bold'>
              Atendiendo a{' '}
              {ticketData ? lastDigits(ticketData?.trackNumber) : 'Sin número'}
            </h2>
            <IconButton onClick={handleCancelAttend}>
              <CloseIcon />
            </IconButton>
          </div>
          <form className='flex flex-col gap-8'>
            {isDocumentNumberRequired && (
              <FormTextInput
                control={control}
                name='documentNumber'
                placeholder='Documento'
              ></FormTextInput>
            )}
            <div className='flex justify-between'>
              <FormSubmitButton
                icon={<CloseIcon />}
                label='Sin gestión'
                isLoading={false}
                action={onFinalizeAsIssue}
                disabled={isFinalizing}
              />
              <FormSubmitButton
                icon={<CheckIcon />}
                label='Finalizar'
                isLoading={false}
                action={handleSubmit((data) => onSubmit(data, true))}
                disabled={isFinalizing}
              />
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
